import { useRef, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import RecordRTC from "recordrtc";
import { updateCandidateResponse } from "../../../../store/slices/miscSlice";
import { useTranscriptionSocket } from "../../../../hooks/useTranscriptionSocket";
import host, { WEBSOCKET_HOST } from "../../../../global";
import { watchHealth } from "../../../../audio-kernel/micKernel";
import { getPreferredAudioConstraints } from "../../../../audio-kernel/micPreference";

function convertFloatTo16BitPCM(float32Array) {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);

  let offset = 0;
  for (let i = 0; i < float32Array.length; i++, offset += 2) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return buffer;
}


export default function useAudioRecorder({
  storingEnabled,
  proctoringVideoRef,
  interviewId,
  onAnswerHealth, // per-answer health report (no-op when absent)
  onLiveMicDeath, // fires once on onEnded / sustained-zero-signal mid-answer (mute skipped — unreliable across drivers)
}) {
  const audioStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const answerVideoRecorderRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  // Per-answer health watcher (guardian)
  const healthWatchRef = useRef(null);
  const healthReportRef = useRef(null);

  const stopHealthWatch = useCallback(
    (endReason) => {
      if (healthWatchRef.current) {
        healthWatchRef.current.stop();
        healthWatchRef.current = null;
      }
      const report = healthReportRef.current;
      healthReportRef.current = null;
      if (report && typeof onAnswerHealth === "function") {
        try {
          onAnswerHealth({
            ...report,
            end_reason: endReason,
            duration_ms: Date.now() - report.started_at,
          });
        } catch (_) {}
      }
    },
    [onAnswerHealth]
  );

  const startHealthWatch = useCallback((stream) => {
    const track = stream.getAudioTracks()[0];
    if (!track) return;
    const label = track.label || "unknown device";
    const tag = () => `MIC GUARDIAN [${label}]`;
    const report = {
      started_at: Date.now(),
      device_label: track.label || null,
      ended: false,
      mute_events: 0,
      unmute_events: 0,
      zero_windows: 0,
      device_changes: 0,
    };
    healthReportRef.current = report;
    let liveDeathReported = false;
    const reportLiveDeath = (reason) => {
      if (liveDeathReported) return; // onEnded + a zero-window can both fire — trip once
      liveDeathReported = true;
      console.warn(`${tag()} TRIP -> recovery screen (reason: ${reason})`);
      if (typeof onLiveMicDeath === "function") {
        try {
          onLiveMicDeath({ reason, device_label: track.label || null });
        } catch (_) {}
      }
    };
    healthWatchRef.current = watchHealth(track, {
      stream,
      onEnded: () => {
        report.ended = true;
        console.warn(`${tag()} track ended (device removed or driver-killed)`);
        reportLiveDeath("track_ended");
      },
      onMute: () => {
        report.mute_events++;
        console.warn(`${tag()} track muted (hardware/driver mute) -> tripping now`);
        reportLiveDeath("track_muted");
      },
      onUnmute: () => {
        report.unmute_events++;
        console.info(`${tag()} track unmuted (no action taken — recording already tripped or continuing)`);
      },
      onDeviceChange: () => {
        report.device_changes++;
        const liveLabel = track.label || "unknown device";
        console.warn(
          `${tag()} devicechange fired mid-answer (#${report.device_changes}) — ` +
          `track still reports label "${liveLabel}", readyState=${track.readyState}, muted=${track.muted}. ` +
          `If this was a "default"-aliased stream, Chrome may have silently swapped the physical capture device.`
        );
      },
      onZeroWindow: ({ zero_seconds }) => {
        report.zero_windows++;
        console.warn(
          `${tag()} ${zero_seconds}s of pure digital silence (telemetry only, trip is at 10s)`
        );
      },
      onZeroTrip: () => reportLiveDeath("zero_signal_10s"),
    });
  }, [onLiveMicDeath]);
  // Clean up all tracks on unmount
  const assemblyTtsEnabled = useSelector((state) => state.miscSlice.assemblyTtsEnabled);
  const { initConnection, sendChunk, closeConnection, flushFinalTranscript } = useTranscriptionSocket();

  useEffect(() => {
    return () => {
      closeConnection(); // Close WebSocket on unmount
      stopHealthWatch("unmount");
      // Unmount mid-answer: the STT context/processor are otherwise only
      // closed by stopAndGetBlobs/stopAll — an SPA route change without
      // them would leave the AudioContext running for the session.
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
      if (recorderRef.current) {
        try {
          recorderRef.current.stopRecording();
        } catch (_) { }
        recorderRef.current = null;
      }
      if (answerVideoRecorderRef.current) {
        try {
          answerVideoRecorderRef.current.stopRecording();
        } catch (_) { }
        answerVideoRecorderRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    initConnection();
  }, []);

  // Dead code below (startold, startWorkingOne): predates this session's
  // work, zero callers anywhere in the app — `start` (further down) is the
  // only live path. Left in place per repo convention (pre-existing dead
  // code isn't silently removed); flagged here for whoever does the cleanup.

  const startold = useCallback(async () => {
    try {
      let audioStream = audioStreamRef.current;

      if (!audioStream || audioStream.getTracks().every((t) => t.readyState === "ended")) {
        audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStreamRef.current = audioStream;
      }

      const audioTrack = audioStream.getAudioTracks()[0];

      const audioRecorder = RecordRTC(audioStream, {
        type: "audio",
        mimeType: "audio/mp3",
      });
      audioRecorder.startRecording();
      recorderRef.current = audioRecorder;

      setIsRecording(true);
      let videoRecorder = null;

      if (storingEnabled && proctoringVideoRef?.current?.srcObject) {
        const videoStream = proctoringVideoRef.current.srcObject;
        const videoTrack = videoStream.getVideoTracks()[0];

        if (videoTrack?.readyState === "live" && audioTrack?.readyState === "live") {
          const combinedStream = new MediaStream([videoTrack, audioTrack]);
          videoRecorder = RecordRTC(combinedStream, {
            type: "video",
            mimeType: "video/mp4;codecs=avc1,mp4a",
          });
          videoRecorder.startRecording();
          answerVideoRecorderRef.current = videoRecorder;
        }
      }

      setError(null);
      return { success: true, audioStream, audioRecorder, videoRecorder };
    } catch (err) {
      console.error("Error starting recording:", err);
      setError(err);
      return { success: false, error: err };
    }
  }, [storingEnabled, proctoringVideoRef]);

  const startWorkingOne = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      audioStreamRef.current = stream;

      // WebSocket to backend
      const ws = new WebSocket(`wss://${WEBSOCKET_HOST}/ws/transcribe`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        // console.log("Live Transcript:", data.text);
      };

      const audioContext = new AudioContext({ sampleRate: 16000 });
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      source.connect(processor);
      processor.connect(audioContext.destination);

      processor.onaudioprocess = (event) => {
        if (!isRecording) return;
        const input = event.inputBuffer.getChannelData(0);
        const pcmData = convertFloatTo16BitPCM(input);

        if (ws.readyState === WebSocket.OPEN) {
          ws.send(pcmData);
        }
      };

      audioContextRef.current = audioContext;
      processorRef.current = processor;

      setIsRecording(true);
      return { success: true };

    } catch (err) {
      console.error("Error starting streaming:", err);
      return { success: false };
    }
  }, []);

  // Live path — the only start*() actually called. Records with the device
  // that passed the mic test (invariant I1); falls back to {audio:true}
  // exactly as before when no preference was persisted.
  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        getPreferredAudioConstraints(interviewId)
      );
      audioStreamRef.current = stream;

      stopHealthWatch("superseded");
      startHealthWatch(stream);

      const audioTrack = stream.getAudioTracks()[0];

      // 1. RecordRTC — the saved answer file
      const audioRecorder = RecordRTC(stream, {
        type: "audio",
        mimeType: "audio/mp3",
      });
      audioRecorder.startRecording();
      recorderRef.current = audioRecorder;

      // 2. Video recording (proctoring, if enabled)
      let videoRecorder = null;
      if (storingEnabled && proctoringVideoRef?.current?.srcObject) {
        const videoStream = proctoringVideoRef.current.srcObject;
        const videoTrack = videoStream.getVideoTracks()[0];

        if (videoTrack?.readyState === "live" && audioTrack?.readyState === "live") {
          const combinedStream = new MediaStream([videoTrack, audioTrack]);
          videoRecorder = RecordRTC(combinedStream, {
            type: "video",
            mimeType: "video/mp4;codecs=avc1,mp4a",
          });
          videoRecorder.startRecording();
          answerVideoRecorderRef.current = videoRecorder;
        }
      }

      // 3. Web Audio → sendChunk, live STT over WebSocket (same stream as #1)
      const audioContext = new AudioContext({ sampleRate: 16000 });
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      source.connect(processor);
      processor.connect(audioContext.destination);

      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        const pcmData = convertFloatTo16BitPCM(input);
        sendChunk(pcmData);
      };

      audioContextRef.current = audioContext;
      processorRef.current = processor;

      setIsRecording(true);
      setError(null);
      return { success: true, audioStream: stream, audioRecorder, videoRecorder };

    } catch (err) {
      console.error("Error starting recording:", err);
      // release a stream acquired before the failure — next start() would strand it
      stopHealthWatch("start_failed");
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
      setError(err);
      return { success: false, error: err };
    }
  }, [storingEnabled, proctoringVideoRef, sendChunk, interviewId, stopHealthWatch, startHealthWatch]);

  // Stop recorders and return blobs
  const stopAndGetBlobs = useCallback(async () => {
    setIsRecording(false);
    stopHealthWatch("answer_complete");
    await new Promise((resolve) => setTimeout(resolve, 600)); // let last chunks flush
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    let audioBlob = null;
    let videoBlob = null;

    const recorder = recorderRef.current;
    const answerVideoRecorder = answerVideoRecorderRef.current;

    if (recorder) {
      if (recorder.state === "recording") {
        await new Promise((resolve) => recorder.stopRecording(resolve));
        audioBlob = recorder.getBlob();
      }
      recorderRef.current = null;
    }

    if (answerVideoRecorder) {
      if (answerVideoRecorder.state === "recording") {
        await new Promise((resolve) => answerVideoRecorder.stopRecording(resolve));
        videoBlob = answerVideoRecorder.getBlob();
      }
      answerVideoRecorderRef.current = null;
    }

    // Release the mic between answers. Previously the stream was left live and
    // the next start() overwrote the ref — one leaked capture per question,
    // and the mic indicator stayed on after the interview.
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }

    return { audioBlob, videoBlob };
  }, [stopHealthWatch]);

  // Hard stop everything
  const stopAll = useCallback(() => {
    stopHealthWatch("stop_all");
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    if (recorderRef.current) {
      if (recorderRef.current.state === "recording") recorderRef.current.stopRecording();
      recorderRef.current = null;
    }
    if (answerVideoRecorderRef.current) {
      if (answerVideoRecorderRef.current.state === "recording") answerVideoRecorderRef.current.stopRecording();
      answerVideoRecorderRef.current = null;
    }
    setIsRecording(false);
  }, [stopHealthWatch]);

  // Stop only the audio stream
  const stopAudioStream = useCallback(() => {
    stopHealthWatch("stream_stopped");
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    } else {
      console.warn("No active audio stream to stop.");
    }
  }, [stopHealthWatch]);

  return {
    start,
    stopAndGetBlobs,
    stopAll,
    stopAudioStream,
    isRecording,
    error,
    audioStreamRef,
    flushFinalTranscript,
  };
}
